/**
 * Before/After Comparison Slider
 *
 * Web component for an interactive comparison slider that allows users
 * to drag between two images (before/after). Supports both mouse and touch.
 */

class BeforeAfterSlider extends HTMLElement {
  constructor() {
    super();
    this.isDragging = false;
    this.position = 0.5;
  }

  connectedCallback() {
    // Parse initial position from data attribute
    this.position = parseFloat(this.dataset.position) || 0.5;

    // Get DOM references
    this.handle = this.querySelector('.before-after-slider__handle');
    this.dragArea = this.querySelector('.before-after-slider__drag-area');
    this.container = this.querySelector('.before-after-slider__container');

    if (!this.handle || !this.container) {
      console.error('[BeforeAfterSlider] Required elements not found');
      return;
    }

    // Bind event handlers
    this.handleDragStart = this.handleDragStart.bind(this);
    this.handleDragMove = this.handleDragMove.bind(this);
    this.handleDragEnd = this.handleDragEnd.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);

    // Add event listeners for handle
    this.handle.addEventListener('mousedown', this.handleDragStart);
    this.handle.addEventListener('touchstart', this.handleDragStart, { passive: false });
    this.handle.addEventListener('keydown', this.handleKeydown);

    // Add event listeners for drag area
    if (this.dragArea) {
      this.dragArea.addEventListener('mousedown', this.handleDragStart);
      this.dragArea.addEventListener('touchstart', this.handleDragStart, { passive: false });
    }

    // Also allow clicking anywhere on the container to set position
    this.container.addEventListener('click', this.handleContainerClick.bind(this));

    // Set initial position
    this.updatePosition(this.position);
  }

  disconnectedCallback() {
    // Clean up event listeners
    if (this.handle) {
      this.handle.removeEventListener('mousedown', this.handleDragStart);
      this.handle.removeEventListener('touchstart', this.handleDragStart);
      this.handle.removeEventListener('keydown', this.handleKeydown);
    }

    if (this.dragArea) {
      this.dragArea.removeEventListener('mousedown', this.handleDragStart);
      this.dragArea.removeEventListener('touchstart', this.handleDragStart);
    }

    // Remove document listeners if dragging
    this.cleanupDragListeners();
  }

  /**
   * Handle drag start (mouse or touch).
   */
  handleDragStart(event) {
    // Prevent default to avoid text selection and scrolling on touch
    event.preventDefault();

    this.isDragging = true;
    this.classList.add('is-dragging');

    // Add document-level listeners for drag
    document.addEventListener('mousemove', this.handleDragMove);
    document.addEventListener('mouseup', this.handleDragEnd);
    document.addEventListener('touchmove', this.handleDragMove, { passive: false });
    document.addEventListener('touchend', this.handleDragEnd);
    document.addEventListener('touchcancel', this.handleDragEnd);

    // Update position immediately
    this.updatePositionFromEvent(event);
  }

  /**
   * Handle drag move.
   */
  handleDragMove(event) {
    if (!this.isDragging) return;

    event.preventDefault();
    this.updatePositionFromEvent(event);
  }

  /**
   * Handle drag end.
   */
  handleDragEnd() {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.classList.remove('is-dragging');

    this.cleanupDragListeners();
  }

  /**
   * Clean up document-level drag listeners.
   */
  cleanupDragListeners() {
    document.removeEventListener('mousemove', this.handleDragMove);
    document.removeEventListener('mouseup', this.handleDragEnd);
    document.removeEventListener('touchmove', this.handleDragMove);
    document.removeEventListener('touchend', this.handleDragEnd);
    document.removeEventListener('touchcancel', this.handleDragEnd);
  }

  /**
   * Handle container click to set position directly.
   */
  handleContainerClick(event) {
    // Don't trigger if clicking on handle or drag area
    if (event.target.closest('.before-after-slider__handle') ||
        event.target.closest('.before-after-slider__drag-area')) {
      return;
    }

    this.updatePositionFromEvent(event);
  }

  /**
   * Handle keyboard navigation.
   */
  handleKeydown(event) {
    const step = event.shiftKey ? 0.1 : 0.05;
    let newPosition = this.position;

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        newPosition = Math.max(0, this.position - step);
        event.preventDefault();
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        newPosition = Math.min(1, this.position + step);
        event.preventDefault();
        break;
      case 'Home':
        newPosition = 0;
        event.preventDefault();
        break;
      case 'End':
        newPosition = 1;
        event.preventDefault();
        break;
      default:
        return;
    }

    this.updatePosition(newPosition);
  }

  /**
   * Calculate position from mouse/touch event.
   */
  updatePositionFromEvent(event) {
    const rect = this.container.getBoundingClientRect();

    // Get client X from mouse or touch event
    let clientX;
    if (event.touches && event.touches.length > 0) {
      clientX = event.touches[0].clientX;
    } else if (event.changedTouches && event.changedTouches.length > 0) {
      clientX = event.changedTouches[0].clientX;
    } else {
      clientX = event.clientX;
    }

    // Calculate position as fraction of container width
    const x = clientX - rect.left;
    const position = Math.max(0, Math.min(1, x / rect.width));

    this.updatePosition(position);
  }

  /**
   * Update the slider position and CSS custom property.
   */
  updatePosition(position) {
    this.position = position;
    this.style.setProperty('--slider-position', `${position * 100}%`);

    // Update ARIA value
    if (this.handle) {
      this.handle.setAttribute('aria-valuenow', Math.round(position * 100));
    }

    // Dispatch custom event for external listeners
    this.dispatchEvent(new CustomEvent('position-change', {
      detail: { position },
      bubbles: true
    }));
  }
}

// Register the custom element
if (!customElements.get('before-after-slider')) {
  customElements.define('before-after-slider', BeforeAfterSlider);
}
